import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, orderBy, Timestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { getISTDate } from "../utils/dateUtils";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";

const RsmSalesReports = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const level = searchParams.get("level") || "cnf_to_dist";

    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [skuMaster, setSkuMaster] = useState({});

    // Date Filters
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                setUserProfile(snap.data());
            } else { navigate("/"); }
        });
        return () => unsub();
    }, [navigate]);

    const loadData = async () => {
        if (!userProfile) return;
        setLoading(true);
        try {
            // Load SKU Master for names
            const skuSnap = await getDocs(collection(db, "skuMaster"));
            const smap = {};
            skuSnap.forEach(d => { smap[d.data().hsn || d.id] = d.data(); });
            setSkuMaster(smap);

            let q;
            const startTimestamp = Timestamp.fromDate(new Date(startDate + "T00:00:00"));
            const endTimestamp = Timestamp.fromDate(new Date(endDate + "T23:59:59"));

            if (level === "cnf_to_dist") {
                q = query(
                    collection(db, "distributor_invoices"),
                    where("clientId", "==", userProfile.clientId),
                    where("source", "==", "CNF_TO_DISTRIBUTOR"),
                    where("createdAt", ">=", startTimestamp),
                    where("createdAt", "<=", endTimestamp),
                    orderBy("createdAt", "desc")
                );
            } else if (level === "dist_to_ds") {
                q = query(
                    collection(db, "orders"),
                    where("clientId", "==", userProfile.clientId),
                    where("source", "==", "DISTRIBUTOR_TO_DS"),
                    where("status", "==", "APPROVED"),
                    where("createdAt", ">=", startTimestamp),
                    where("createdAt", "<=", endTimestamp),
                    orderBy("createdAt", "desc")
                );
            } else if (level === "ds_to_outlet") {
                q = query(
                    collection(db, "Outlet_Orders"),
                    where("clientId", "==", userProfile.clientId),
                    where("createdAt", ">=", startTimestamp),
                    where("createdAt", "<=", endTimestamp),
                    orderBy("createdAt", "desc")
                );
            }

            const snap = await getDocs(q);
            const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setData(arr);
        } catch (err) {
            console.error(err);
            alert("Error loading reports: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [userProfile, level, startDate, endDate]);

    const stats = useMemo(() => {
        let totalVal = 0;
        let totalQty = 0;
        data.forEach(r => {
            if (r.status === "VOID") return; // Skip voided records from totals
            totalVal += (Number(r.totalAmount) || 0);
            totalQty += (level === "cnf_to_dist" ? (Number(r.financialSummary?.tQty) || Number(r.totalQty) || 0) : (Number(r.totalQty) || 0));
        });
        return { totalVal, totalQty, count: data.length };
    }, [data]);

    const exportToExcel = () => {
        if (!data || data.length === 0) {
            alert("No data available to export");
            return;
        }

        const exportRows = [];
        for (let i = 0; i < data.length; i++) {
            const row = data[i];
            const baseData = {};
            const keys = Object.keys(row);
            for (let j = 0; j < keys.length; j++) {
                const k = keys[j];
                const v = row[k];
                if (v && typeof v === 'object') {
                    if (v.seconds !== undefined) {
                        const d = new Date(v.seconds * 1000);
                        baseData[k] = d.toLocaleString();
                    }
                } else {
                    baseData[k] = v;
                }
            }

            const hsnSum = row.hsnSummary;
            const itemLines = row.itemLines;
            let lines = [];

            if (hsnSum && typeof hsnSum === 'object') {
                const hsnKeys = Object.keys(hsnSum);
                for (let j = 0; j < hsnKeys.length; j++) {
                    const hsn = hsnKeys[j];
                    const info = hsnSum[hsn];
                    lines.push({
                        hsn: hsn,
                        itemName: info.name || "",
                        qty: info.qty || 0,
                        basic: info.basic || 0,
                        gstAmt: info.gstAmt || 0
                    });
                }
            } else if (itemLines && Array.isArray(itemLines)) {
                for (let j = 0; j < itemLines.length; j++) {
                    const li = itemLines[j];
                    lines.push({
                        hsn: li.hsn || "",
                        itemName: li.skuName || li.sku || "",
                        qty: li.qty || 0,
                        basic: li.basicAmount || li.basic || 0,
                        gstAmt: (li.cgstAmount || 0) + (li.sgstAmount || 0) + (li.igstAmount || 0) || (li.gstAmount || 0)
                    });
                }
            }

            if (lines.length === 0) {
                exportRows.push(baseData);
            } else {
                for (let j = 0; j < lines.length; j++) {
                    exportRows.push({ ...baseData, ...lines[j] });
                }
            }
        }

        const sequence = [
            "createdAt", "approvedDateOnly", "source", "partyType", "distributorId",
            "partyName", "displayOrderId", "hsn", "itemName", "qty", "basic", "gstAmt", "totalAmount"
        ];

        const finalFormatted = [];
        for (let i = 0; i < exportRows.length; i++) {
            const r = exportRows[i];
            const formatted = {};
            // 1. Apply Sequence
            for (let j = 0; j < sequence.length; j++) {
                const key = sequence[j];
                let header = key.toUpperCase();
                if (header === "APPROVEDDATEONLY") header = "DATE";
                if (header === "DISPLAYORDERID") header = "ORDER ID";
                formatted[header] = r[key] === undefined ? "" : r[key];
            }
            // 2. Append other fields (excluding internal JSON)
            const rKeys = Object.keys(r);
            for (let j = 0; j < rKeys.length; j++) {
                const key = rKeys[j];
                const header = key.toUpperCase();
                const excluded = ["HSNSUMMARY", "ITEMLINES", "ITEMS", "FINANCIALSUMMARY"];
                if (formatted[header] === undefined && excluded.indexOf(header) === -1) {
                    formatted[header] = r[key];
                }
            }
            finalFormatted.push(formatted);
        }

        const ws = XLSX.utils.json_to_sheet(finalFormatted);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Raw Data");
        XLSX.writeFile(wb, `Sales_Flattened_Dump_${level}.xlsx`);
    };

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
                    <div>
                        <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em" }}>Sales Intelligence</h1>
                        <p style={{ color: "var(--text-muted)" }}>Consolidated transaction audit and multi-layer supply chain reports.</p>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                        <button onClick={exportToExcel} className="btn-secondary" style={{ display: "flex", alignItems: "center", gap: 8, background: "white" }}>
                            <FaFileExcel style={{ fontSize: 18 }} />
                            Export Excel
                        </button>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="premium-card" style={{ marginBottom: 32, padding: 24, display: "flex", gap: 24, alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: 12 }}>Select Report Level</label>
                        <div style={{ display: "flex", gap: 8, background: "#f1f5f9", padding: 4, borderRadius: 12, width: "fit-content" }}>
                            {[
                                { id: "cnf_to_dist", label: "C&F to Distributor" },
                                { id: "dist_to_ds", label: "Distributor to DS" },
                                { id: "ds_to_outlet", label: "DS to Retail" }
                            ].map(tab => (
                                <div
                                    key={tab.id}
                                    onClick={() => navigate(`/rsm/sales-reports?level=${tab.id}`)}
                                    style={{
                                        padding: "10px 20px",
                                        borderRadius: 10,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        background: level === tab.id ? "white" : "transparent",
                                        color: level === tab.id ? "var(--primary)" : "var(--text-muted)",
                                        boxShadow: level === tab.id ? "0 4px 12px rgba(0,0,0,0.05)" : "none"
                                    }}
                                >
                                    {tab.label}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: 8 }}>From Date</label>
                        <input className="premium-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: 8 }}>To Date</label>
                        <input className="premium-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                </div>

                {/* STATS */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 32 }}>
                    <div className="premium-card" style={{ padding: 24 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>TOTAL VALUATION</div>
                        <div style={{ fontSize: 24, fontWeight: 900, color: "var(--primary)" }}>₹{stats.totalVal.toLocaleString()}</div>
                    </div>
                    <div className="premium-card" style={{ padding: 24 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>TOTAL UNITS</div>
                        <div style={{ fontSize: 24, fontWeight: 900, color: "var(--success)" }}>{stats.totalQty}</div>
                    </div>
                    <div className="premium-card" style={{ padding: 24 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>DOCUMENT COUNT</div>
                        <div style={{ fontSize: 24, fontWeight: 900 }}>{stats.count}</div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                    <table className="enterprise-table">
                        <thead>
                            <tr>
                                <th>Date / Order ID</th>
                                <th>Entity Mapping</th>
                                <th style={{ textAlign: "right" }}>Basic</th>
                                <th style={{ textAlign: "right" }}>Gst</th>
                                <th style={{ textAlign: "right" }}>Total Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" style={{ textAlign: "center", padding: 48, fontWeight: 700 }}>Syndicating Transaction Data...</td></tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan="5" style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>No transaction data found for selected criteria.</td></tr>
                            ) : data.map(r => (
                                <tr key={r.id}>
                                    <td>
                                        <div style={{ fontWeight: 800 }}>{r.createdAt instanceof Timestamp ? r.createdAt.toDate().toLocaleDateString() : r.date || "N/A"}</div>
                                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.displayOrderId || r.id}</div>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 700 }}>{r.partyName}</div>
                                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.partyType || "DEALER"} • {r.source}</div>
                                    </td>
                                    <td style={{ textAlign: "right" }}>₹{(level === "cnf_to_dist" ? (Number(r.financialSummary?.tBasic) || 0) : (Number(r.totalBasic) || 0)).toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{(level === "cnf_to_dist" ? (Number(r.financialSummary?.tGst) || 0) : (Number(r.totalAmount - (r.totalBasic || 0)) || 0)).toLocaleString()}</td>
                                    <td style={{ textAlign: "right", fontWeight: 800, color: "var(--primary)" }}>₹{(Number(r.totalAmount) || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default RsmSalesReports;
